"use client";

import { useTranslations } from "next-intl";
import { useLearnPriceRegion } from "@/features/learn/lib/price-region";

/**
 * The Learn Pro card's prices (`LN-BUY01`, founder 2026-10-08): a visitor in
 * Bangladesh sees the taka year first, with the card prices beneath it; anyone
 * else sees the dollar prices alone. The card around it stays a server
 * component; only this part needs to know where the visitor is.
 *
 * With no taka price on sale (`yearlyBdt` null) every visitor gets the dollar
 * layout, which is what the card always showed.
 */
export function LearnProPrices({
  layout,
  monthly,
  yearly,
  yearlyBdt,
}: {
  layout: "band" | "compact";
  monthly: string;
  yearly: string;
  yearlyBdt: string | null;
}) {
  const t = useTranslations("learn.pro");
  const region = useLearnPriceRegion();
  const takaFirst = region === "bd" && yearlyBdt !== null;

  if (layout === "compact") {
    return takaFirst ? (
      <>
        <p className="mt-3 font-[family-name:var(--font-heading)] text-lg font-bold text-[rgb(var(--foreground))]">
          {t("yearlyTaka", { yearlyBdt })}
        </p>
        <p className="mt-1 text-xs text-neutral-600">{t("byCard", { monthly, yearly })}</p>
      </>
    ) : (
      <p className="mt-3 font-[family-name:var(--font-heading)] text-base font-bold text-[rgb(var(--foreground))]">
        {t("priceLine", { monthly, yearly })}
      </p>
    );
  }

  if (takaFirst) {
    return (
      <>
        <p className="flex items-baseline gap-1.5">
          <span className="font-[family-name:var(--font-heading)] text-4xl font-bold tracking-tight text-[rgb(var(--foreground))]">
            {yearlyBdt}
          </span>
          <span className="text-sm text-neutral-600">{t("perYear")}</span>
        </p>
        <p className="mt-1.5 text-[13px] text-neutral-600">{t("takaTerm")}</p>
        <div className="my-3.5 h-px bg-neutral-100" />
        <p className="font-[family-name:var(--font-heading)] text-xs font-bold uppercase tracking-[0.1em] text-neutral-500">
          {t("byCardLabel")}
        </p>
        <p className="mt-1.5 flex flex-wrap items-baseline gap-x-1.5 font-[family-name:var(--font-heading)] text-lg font-bold text-[rgb(var(--foreground))]">
          {monthly}
          <span className="font-sans text-[13px] font-normal text-neutral-600">{t("perMonth")}</span>
          <span aria-hidden className="px-1 font-normal text-neutral-300">
            ·
          </span>
          {yearly}
          <span className="font-sans text-[13px] font-normal text-neutral-600">{t("perYear")}</span>
        </p>
      </>
    );
  }

  return (
    <>
      <p className="flex items-baseline gap-1.5">
        <span className="font-[family-name:var(--font-heading)] text-4xl font-bold tracking-tight text-[rgb(var(--foreground))]">
          {monthly}
        </span>
        <span className="text-sm text-neutral-600">{t("perMonth")}</span>
      </p>
      <div className="my-3.5 h-px bg-neutral-100" />
      <p className="flex items-baseline gap-1.5">
        <span className="font-[family-name:var(--font-heading)] text-4xl font-bold tracking-tight text-[rgb(var(--foreground))]">
          {yearly}
        </span>
        <span className="text-sm text-neutral-600">{t("perYear")}</span>
      </p>
    </>
  );
}
